// @flow

import { useState } from 'react';

import type { FormApi } from 'final-form';

import { useDispatch } from 'react-redux';

import { selectProductsByType } from '@gandi/cart';
import { Form } from '@gandi/form/FinalForm';
import { notifyDanger } from '@gandi/notifications';
import { useTranslator } from '@gandi/translate';

import { Badge, Button } from '@gandi/ui';

import { useAppSelector } from 'src/hooks';
import type { ProductSummary } from 'src/modules/mailbox-standalone/resources/products';

import { useProductPrice } from '../../../modules/hooks/useProductPrice';
import { useUpsertCartMailboxes } from '../../../modules/hooks/useUpsertCartMailboxes';
import Price from '../../Price';

import styles from './SummaryChoice.scss';

const productType = { productType: 'mailbox-standalone' };

type FormValues = {
  autorenew: string,
  config_name: string,
  duration_month: number,
};

type LoadingConfig = {
  [string]: boolean,
};

export function SummaryChoice({
  product,
  FORM_ID,
}: {
  product: ProductSummary,
  FORM_ID: string,
}): React.MixedElement {
  const { __, formatNumber } = useTranslator();
  const cartProducts = useAppSelector(state =>
    selectProductsByType(state, productType),
  );

  const { handleUpsertCartMailboxes } = useUpsertCartMailboxes();
  const dispatch = useDispatch();

  async function handleSubmit(
    { config_name, duration_month }: FormValues,
    form: FormApi<FormValues>,
  ) {
    const payload = {
      config_name,
      duration_month: Number(duration_month),
      shopping: {
        quota_kb: product.config[0].quota_kb,
      },
    };

    const alreadyAddedProduct = cartProducts.find(
      item =>
        item.payload.config_name === payload.config_name &&
        item.payload.duration_month === payload.duration_month,
    );

    try {
      await handleUpsertCartMailboxes(
        {
          ...payload,
          quantity: 1 + (alreadyAddedProduct?.payload.quantity ?? 0),
          autorenew: alreadyAddedProduct?.payload.autorenew ?? true,
        },
        alreadyAddedProduct?.id,
      );
      form.reset();
    } catch (err) {
      dispatch(
        notifyDanger({
          message:
            err.response?.data.message ||
            __('We are unable to add mailboxes to your cart.'),
        }),
      );
    }
  }

  const showAction = cartProducts.find(
    item => item.payload.config_name === product.config_name,
  );

  const { label, quota_kb } = product.config[0];

  // convert bytes → megabytes (MB)
  const storage = quota_kb / 1024 / 1024;

  const emailNumber = formatNumber(Math.ceil(5000 * storage));

  const priceProps = useProductPrice({
    product,
    minDuration: 12,
    // Display default price per month even for yearly billing
    duration: 1,
  });

  const [loadingConfig, setLoadingConfig] = useState<LoadingConfig>({});

  return (
    <Form
      key={product.config_name}
      id={`${FORM_ID}-${product.config_name}`}
      onSubmit={async (values, form) => {
        setLoadingConfig(prev => ({
          ...prev,
          [product.config_name]: true,
        }));
        await handleSubmit(values, form);
        setLoadingConfig(prev => ({
          ...prev,
          [product.config_name]: false,
        }));
      }}
      initialValues={{
        config_name: product.config_name,
        autorenew: 'true',
        duration_month: 12,
      }}
    >
      <div className={styles.productSummary}>
        <div className={styles.productInfo}>
          <Badge
            intent={
              product.config_name === 'standard'
                ? 'default'
                : product.config_name === 'premium'
                  ? 'primary'
                  : 'success'
            }
          >
            {label}
          </Badge>

          <div className={styles.productSubtitleContainer}>
            <p className={styles.productTitle}>
              {/* eslint-disable-next-line  */}
              {__('%(storage)s storage per mailbox', {
                storage: `${formatNumber(storage)} ${__(' GB ')}`,
              })}
            </p>

            <p className={styles.productSubtitle}>
              {__('Approximately %(countEmails)s emails', {
                countEmails: emailNumber,
              })}
            </p>
          </div>
        </div>

        {!showAction && (
          <div className={styles.productAction}>
            <div className={styles.productPriceContainer}>
              <Price {...priceProps} subtitle={__('/month')} singleLine />
            </div>
            <Button
              form={`${FORM_ID}-${product.config_name}`}
              loading={!!loadingConfig[product.config_name]}
              small
            >
              {__('Add to cart')}
            </Button>
          </div>
        )}
      </div>
    </Form>
  );
}
